import { useEffect, useMemo, useState } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
  Spinner,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { Formik, Form } from 'formik';
import { MODALS } from '@/configs/modals';
import { useModals } from '@/hooks/useModals';
import { flatten, get, isArray, isEmpty, omit, showErrors, uniq, SLUG_REGEX } from '@/utils/helpers';
import FormikReactSelect from '@/components/FormikSelect';
import FormikPassword from '@/components/FormikPassword';
import FormikInput from '@/components/FormikInput';
import { fetchCenters } from '@/store/centers/actions';
import { getCentersOptions, getFeaturesLanguageOptions, getFeaturesTagOptions } from '@/utils/selectors';
import FormikCheckbox from '@/components/FormikCheckbox';
import FormikCheckboxGroup from '@/components/FormikCheckboxGroup';
import FormikPhotoUpload from '@/components/FormikPhotoUpload';
import {
  managePsychologist,
  fetchPsychologistApi,
  fetchFiltersApi,
} from '@/store/psychologist/actions';
import { refreshCache } from '@/store/base/actions';
import { FILTERS_TYPES_ARRAY } from '@/configs';
import FormikVideoUpload from '@/components/FormikVideoUpload';
import FormikDiplomasUpload from '@/components/FormikDiplomasUpload';
import FormikSlugInput from '@/components/FormikSlugInput';

const PsychologistManageDrawer = () => {
  const modalId = MODALS.psychologist;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { params, modalClose } = useModals(modalId);
  const { isOpen, id } = params || {};
  const isEdit = !!id;

  const closeModal = () => {
    modalClose(modalId);
  };
  const handleCloseModal = () => {
    setPsychologist({});
  };

  const [isLoading, setLoading] = useState(false);
  const [psychologist, setPsychologist] = useState({});
  useEffect(() => {
    if (!!id && isOpen) {
      setLoading(true);
      const fetchInitData = async () => {
        const res = await fetchPsychologistApi(id);
        if (!!res) {
          setPsychologist(res);
        }
        setLoading(false);
      };
      fetchInitData();
    }
  }, [id, dispatch, isOpen]);

  const centersOptions = useSelector(getCentersOptions);
  useEffect(() => {
    isOpen && dispatch(fetchCenters());
  }, [dispatch, isOpen]);

  const [filtersOptionsGroup, setFiltersOptionsCroup] = useState({});
  useEffect(() => {
    if (isOpen) {
      const fetchInitFilters = async () => {
        const res = await fetchFiltersApi();
        const filtersGrouped = res.reduce((a, c) => {
          if (!c || !c?.type) return a;
          if (!c?.is_active) return a;
          const item = { label: c.text, value: c.id };
          a[c?.type] = [...(a[c?.type] || []), item];
          a['byId'] = {
            ...(a['byId'] || {}),
            [c.id]: c
          }
          return a
        }, {})
        if (!!res) {
          setFiltersOptionsCroup(filtersGrouped);
        }
      };
      fetchInitFilters();
    }
  }, [isOpen]);

  const languagesOptions = useSelector(getFeaturesLanguageOptions);
  const featuresTagOptions = useSelector(getFeaturesTagOptions);

  const onSubmit = async (values, actions) => {
    const filteredNormalized = !isEmpty(values?.filters) ? Object.values(values?.filters) : [];
    const formData = {
      ...omit(values, ['filters', 'filter_ids', 'features', 'languages', 'videoInit', 'diplomas']),
      filter_ids: flatten(filteredNormalized) || [],
      features: uniq([...(values?.features || []), ...(values?.languages || [])]),
    };
    if (!isEdit) {
      formData.diplomas = values?.diplomas;
    }
    return await managePsychologist({
      id,
      data: formData,
      dispatch,
      onSuccess: (res) => {
        isEdit
          ? toast.success(t('psychologist.modal.update_success'))
          : toast.success(t('psychologist.modal.create_success'));
        closeModal();
        refreshCache();
      },
      onError: (error = {}) => {
        showErrors(error, actions);
      },
    });
  };

  const initFiledFilters = useMemo(() => {
    if (isEmpty(filtersOptionsGroup) || isEmpty(psychologist)) return null;
    const filterIds = get(psychologist, ['filter_ids'], []);
    return filterIds?.reduce((a, id) => {
      const item = get(filtersOptionsGroup, ['byId', id])
      if (!item) return a;
      a[item.type] = [...(a[item.type] || []), item.id];
      return a
    }, {});
  }, [psychologist, filtersOptionsGroup]);

  const initFiledLanguages = useMemo(() => {
    if (isEmpty(languagesOptions) || isEmpty(psychologist)) return [];
    return psychologist?.features?.filter((_id) => languagesOptions?.find(i => Number(i.value) === Number(_id)));
  }, [languagesOptions, psychologist]);

  const initFiledFeatures = useMemo(() => {
    if (isEmpty(featuresTagOptions) || isEmpty(psychologist)) return [];
    return psychologist?.features?.filter((_id) => featuresTagOptions?.find(i => Number(i.value) === Number(_id)));
  }, [featuresTagOptions, psychologist]);

  const initialValues = useMemo(
    () => {
      return {
        activate: get(psychologist, ['activate'], false),
        online: get(psychologist, ['online'], false),
        offline: get(psychologist, ['offline'], false),
        filter_ids: get(psychologist, ['filter_ids'], []),
        centers: get(psychologist, ['centers'], []),
        fullname_uk: psychologist?.fullname_uk || '',
        slug: psychologist?.slug || '',
        telegram_id: psychologist?.telegram_id || '',
        description_uk: psychologist?.description_uk || '',
        seo_description_uk: psychologist?.seo_description_uk || '',
        seo_title_uk: psychologist?.seo_title_uk || '',
        life_credo_uk: psychologist?.life_credo_uk || '',
        help_with_uk: psychologist?.help_with_uk || '',
        work_principles_uk: psychologist?.work_principles_uk || '',
        values_uk: psychologist?.values_uk || '',
        higher_education_uk: psychologist?.higher_education_uk || '',
        professional_courses_uk: psychologist?.professional_courses_uk || '',
        another_uk: psychologist?.another_uk || '',
        fullname_ru: psychologist?.fullname_ru || '',
        description_ru: psychologist?.description_ru || '',
        seo_description_ru: psychologist?.seo_description_ru || '',
        seo_title_ru: psychologist?.seo_title_ru || '',
        life_credo_ru: psychologist?.life_credo_ru || '',
        help_with_ru: psychologist?.help_with_ru || '',
        work_principles_ru: psychologist?.work_principles_ru || '',
        keywords_uk: psychologist?.keywords_uk || '',
        keywords_ru: psychologist?.keywords_ru || '',
        values_ru: psychologist?.values_ru || '',
        higher_education_ru: psychologist?.higher_education_ru || '',
        professional_courses_ru: psychologist?.professional_courses_ru || '',
        another_ru: psychologist?.another_ru || '',
        problems: psychologist?.problems?.map((i) => i?.id) || [],
        photo: '',
        photoInit: get(psychologist, ['photo', 'sm']),
        experience: dayjs(get(psychologist, ['experience'], dayjs())).format(
          'YYYY-MM-DD'
        ),
        filters: initFiledFilters,
        ordering: psychologist?.ordering || 0,
        consultation_count: psychologist?.consultation_count || 0,
        is_junior: get(psychologist, ['is_junior'], false),
        is_tester: get(psychologist, ['is_tester'], false),
        is_vip: get(psychologist, ['is_vip'], false),
        is_pair_consultation: get(psychologist, ['is_pair_consultation'], false),
        is_banner: get(psychologist, ['is_banner'], false),
        features: initFiledFeatures || [],
        languages: initFiledLanguages || [],
        video_desktop: psychologist?.video_desktop || '',
        video_mobile: psychologist?.video_mobile || '',
        diplomas: psychologist?.diplomas || [],
      };
    },
    [psychologist, initFiledFilters, initFiledFeatures, initFiledLanguages]
  );

  if (!isEdit) {
    initialValues.user = {
      username: '',
      password: '',
      fullname_uk: '',
      fullname_ru: '',
    };
  }

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={closeModal}
      direction="end"
      className={modalId}
      onClosed={handleCloseModal}
      keyboard={false}
    >
      <OffcanvasHeader toggle={closeModal}>
        {isEdit
          ? t('psychologist.modal.edit_title')
          : t('psychologist.modal.add_title')}
      </OffcanvasHeader>
      <OffcanvasBody
        className={classNames('psh-manage', { 'is-loading': isLoading })}
      >
        {isLoading && (
          <Spinner color="primary" type="grow" className="psh-manage-spinner" />
        )}
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          enableReinitialize={true}
          validate={({ slug }) => (
            !!slug && !SLUG_REGEX.test(slug) ? { slug: t('validation.slug') } : {}
          )}
          // validationSchema={Yup.object({
          //   username: Yup.string().required('Please Enter Email'),
          // })}
        >
          {({ errors, isSubmitting, values, ...other }) => (
            <Form className="psh-manage-form needs-validation">
              {!isEdit && (
                <Card className="psh-manage-user-block">
                  <CardHeader>
                    <CardTitle>{t('psychologist.modal.user_title')}</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div className="fields-grid-2">
                      <FormikInput name="user.username" label="Email" />
                      <FormikPassword
                        name="user.password"
                        label={t('psychologist.modal.password')}
                      />
                      <FormikInput
                        name="user.fullname_uk"
                        label={t('psychologist.modal.name') + ' (uk)'}
                      />
                      <FormikInput
                        name="user.fullname_ru"
                        label={t('psychologist.modal.name') + ' (ru)'}
                      />
                    </div>
                    {!!errors?.user && (
                      <div className="form-error mt-3">
                        {t('validation.psh_user')}
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
              <Card className="psh-manage-psh-block">
                <CardHeader>
                  <CardTitle>{t('psychologist.modal.psh_title')}</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="fields-grid-2">
                    <FormikPhotoUpload
                      name="photo"
                      initName="photoInit"
                      className="psh-avatar"
                      uploadType="base64"
                      description={t('field_upload_photo_text')}
                    />

                    <div>
                      <FormikCheckbox
                        name="activate"
                        label={t('psychologist.modal.active_profile')}
                        className="mb-1"
                      />
                      <FormikCheckbox
                        name="online"
                        label={t('psychologist.modal.online')}
                        className="mb-1"
                      />
                      <FormikCheckbox
                        name="offline"
                        label={t('psychologist.modal.offline')}
                        className="mb-1"
                      />
                      <FormikCheckbox
                        name="is_junior"
                        label={t('psychologist.modal.is_junior')}
                        className="mb-1"
                      />
                      <FormikCheckbox
                        name="is_tester"
                        label={t('psychologist.modal.is_tester')}
                        className="mb-1"
                      />
                      <FormikCheckbox
                        name="is_vip"
                        label={t('psychologist.modal.is_vip')}
                        className="mb-1"
                      />
                      <FormikCheckbox
                        name="is_pair_consultation"
                        label={t('psychologist.modal.is_pair_consultation')}
                        className="mb-1"
                      />
                      <FormikCheckbox
                          name="is_banner"
                          label={t('psychologist.modal.is_banner')}
                          className="mb-1"
                      />
                    </div>
                    <FormikInput
                      name="telegram_id"
                      label={`${t('psychologist.modal.telegram_id')}`}
                    />
                    <FormikInput
                      name="ordering"
                      type="number"
                      label={t('psychologist.modal.ordering')}
                    />
                    <FormikInput
                      name="fullname_uk"
                      label={`${t('psychologist.modal.name')} (uk)`}
                    />
                    <FormikInput
                      name="fullname_ru"
                      label={`${t('psychologist.modal.name')} (ru)`}
                    />
                    <FormikSlugInput
                      sourceNames={['fullname_uk', 'user.fullname_uk']}
                    />
                    <div />
                    <FormikInput
                      name="description_uk"
                      label={`${t('psychologist.modal.description')} (uk)`}
                    />
                    <FormikInput
                      name="description_ru"
                      label={`${t('psychologist.modal.description')} (ru)`}
                    />
                    <FormikInput
                      name="experience"
                      label={t('psychologist.experience')}
                      type="date"
                    />
                    <FormikReactSelect
                      name="gender"
                      label={t('psychologist.gender')}
                      placeholder=""
                      options={[
                        {
                          label: t('psychologist.male'),
                          value: 'male',
                        },
                        { label: t('psychologist.female'), value: 'female' },
                      ]}
                    />

                    <FormikInput
                      name="consultation_count"
                      type="number"
                      label={t('psychologist.modal.consultation_count')}
                    />
                  </div>
                  <div className="fields-grid-2 mt-4">
                    <FormikCheckboxGroup
                      name="languages"
                      items={languagesOptions}
                      title={t('psychologist.modal.use_languages')}
                      typeValue="number"
                    />
                    <FormikCheckboxGroup
                      name="features"
                      items={featuresTagOptions}
                      title={t('psychologist.modal.features')}
                      typeValue="number"
                    />
                  </div>
                  <div className="fields-grid-2">
                    <div>
                      <FormikCheckboxGroup
                        name="centers"
                        items={centersOptions}
                        title={t('psychologist.modal.centers')}
                        className="my-4"
                        typeValue="number"
                      />
                    </div>
                  </div>
                  <div className="fields-grid-2">
                    <FormikInput
                      type="textarea"
                      name="life_credo_uk"
                      label={`${t('psychologist.life_credo')} (uk)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="life_credo_ru"
                      label={`${t('psychologist.life_credo')} (ru)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="help_with_uk"
                      label={`${t('psychologist.help_with')} (uk)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="help_with_ru"
                      label={`${t('psychologist.help_with')} (ru)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="work_principles_uk"
                      label={`${t('psychologist.work_principles')} (uk)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="work_principles_ru"
                      label={`${t('psychologist.work_principles')} (ru)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="values_uk"
                      label={`${t('psychologist.values')} (uk)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="values_ru"
                      label={`${t('psychologist.values')} (ru)`}
                    />
                  </div>
                </CardBody>
              </Card>

              <Card className="psh-manage-filters-block">
                <CardHeader>
                  <CardTitle>{t('psychologist.education')}</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="fields-grid-2">
                    <FormikInput
                      type="textarea"
                      name="higher_education_uk"
                      label={`${t('psychologist.higher_education')} (uk)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="higher_education_ru"
                      label={`${t('psychologist.higher_education')} (ru)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="professional_courses_uk"
                      label={`${t('psychologist.professional_courses')} (uk)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="professional_courses_ru"
                      label={`${t('psychologist.professional_courses')} (ru)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="another_uk"
                      label={`${t('psychologist.another')} (uk)`}
                    />
                    <FormikInput
                      type="textarea"
                      name="another_ru"
                      label={`${t('psychologist.another')} (ru)`}
                    />
                  </div>
                  <FormikDiplomasUpload
                    name="diplomas"
                    label="Дипломи"
                    className="psh-diplomas mt-4"
                    isUpdateMode={isEdit}
                    psychologistId={id}
                  />
                </CardBody>
              </Card>

              <Card className="psh-manage-filters-block">
                <CardHeader>
                  <CardTitle>{t('psychologist.modal.video')}</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="fields-grid-2">
                    <FormikVideoUpload
                      name="video_desktop"
                      label="Відео (основне)"
                      className="psh-avatar"
                    />
                    <FormikVideoUpload
                      name="video_mobile"
                      label="Відео (мобільне)"
                      className="psh-avatar"
                    />
                  </div>
                </CardBody>
              </Card>

              <Card className="psh-manage-filters-block">
                <CardHeader>
                  <CardTitle>{t('psychologist.modal.filters')}</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="fields-grid-2">
                    {FILTERS_TYPES_ARRAY.map(
                      (item) =>
                        isArray(filtersOptionsGroup[item]) && (
                          <FormikReactSelect
                            name={`filters[${item}]`}
                            label={t(`filters.types.${item}`)}
                            placeholder=""
                            options={filtersOptionsGroup[item]}
                            className="my-2"
                            isMulti
                            isClearable={false}
                            closeMenuOnSelect={false}
                            key={item}
                          />
                        )
                    )}
                  </div>
                </CardBody>
              </Card>

              <Card className="psh-manage-seo-block">
                <CardHeader>
                  <CardTitle>SEO (uk)</CardTitle>
                </CardHeader>
                <CardBody>
                  <FormikInput
                    name="seo_title_uk"
                    label={t('seo_title') + ' (uk)'}
                  />
                  <div className="fields-grid-2">
                    <FormikInput
                      type="textarea"
                      name="seo_description_uk"
                      label={t('seo_description') + ' (uk)'}
                    />
                    <FormikInput
                      type="textarea"
                      name="keywords_uk"
                      label={t('seo_keywords') + ' (uk)'}
                    />
                  </div>
                </CardBody>
              </Card>
              <Card className="psh-manage-seo-block">
                <CardHeader>
                  <CardTitle>SEO (ru)</CardTitle>
                </CardHeader>
                <CardBody>
                  <FormikInput
                    name="seo_title_ru"
                    label={t('seo_title') + ' (ru)'}
                  />
                  <div className="fields-grid-2">
                    <FormikInput
                      type="textarea"
                      name="seo_description_ru"
                      label={t('seo_description') + ' (ru)'}
                    />
                    <FormikInput
                      type="textarea"
                      name="keywords_ru"
                      label={t('seo_keywords') + ' (ru)'}
                    />
                  </div>
                </CardBody>
              </Card>

              <div className="flex jcfe mb-3">
                <Button
                  color="danger"
                  className="waves-effect waves-light mx-1 px-4"
                  type="button"
                  disabled={isSubmitting}
                  onClick={closeModal}
                >
                  <span>{t('psychologist.modal.btn_cancel')}</span>
                </Button>
                <Button
                  color="primary"
                  className="waves-effect waves-light mx-1 px-5"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Spinner size="sm" className="mr-1" />}
                  <span>{t('psychologist.modal.btn_save')}</span>
                </Button>
              </div>
              {!isEmpty(errors) && (
                <div className="flex jcfe mb-3 form-error ">
                  {t('form_has_error')}
                </div>
              )}
              {!!get(errors, ['details']) && (
                <div className="flex jcfe mb-3 form-error">
                  {get(errors, ['details'])}
                </div>
              )}
            </Form>
          )}
        </Formik>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default PsychologistManageDrawer;
